import { useEffect } from "react";
import { RegisterOptions, useFormContext } from "react-hook-form";

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { FormCurrencySlider, FormSelectField } from "@covertree/ui";
import {
  CustomizePlanCoverage,
  PolicyCustomizationForm,
  ValidationKey,
} from "@covertree/new-business-forms";
import { CardMedia } from "@covertree/ui/cardMedia";
import {
  InputLabelHeight,
  type OptionType as GenericOptionType,
} from "@covertree/types";
import { formatCustomizationOptions } from "@covertree/utils";
import { SettlementOption } from "@covertree/new-business-mappings";

export type CoverageRules = Partial<
  Record<ValidationKey, Pick<RegisterOptions, "validate">>
>;

interface CoveragesCustomizationProps {
  coverages: CustomizePlanCoverage[];
  loading?: boolean;
  /** Field rules, for forms without a resolver. */
  rules?: CoverageRules;
}

export function CoveragesCustomization({
  coverages,
  loading,
  rules,
}: CoveragesCustomizationProps) {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const { control, watch, getValues, setValue } =
    useFormContext<PolicyCustomizationForm>();

  const covBValuePath = "units.0.unitLevelCoverages.coverageB_value";
  const covCValuePath = "units.0.unitLevelCoverages.coverageC_value";
  const covBSettlementOptionPath =
    "units.0.unitLevelCoverages.coverageB_settlementOption";
  const covCSettlementOptionPath =
    "units.0.unitLevelCoverages.coverageC_settlementOption";

  const covCValue = watch(covCValuePath);
  const covBValue = watch(covBValuePath);
  const covBSettlementOption = watch(covBSettlementOptionPath);
  const covCSettlementOption = watch(covCSettlementOptionPath);

  // Set default values for Coverage B/C fields if undefined
  useEffect(() => {
    if (covBValue === undefined && covBSettlementOption) {
      setValue(covBValuePath, 0 as number);
    }

    if (covCValue === undefined && covCSettlementOption) {
      setValue(covCValuePath, 0 as number);
    }
  }, [
    covBValue,
    covCValue,
    covBSettlementOption,
    covCSettlementOption,
    setValue,
  ]);

  return (
    <>
      {coverages.map(
        ({
          validationKey,
          details,
          step,
          options,
          disabled,
          fieldType,
          min,
          max,
        }: CustomizePlanCoverage) => (
          <Card
            key={validationKey}
            sx={(theme) => ({ marginTop: theme.spacing(3), display: "flex" })}>
            {!isMobile && (
              <CardMedia
                sx={(theme) => ({
                  margin: theme.spacing(3),
                })}
                image={details.image}
                data-testid={`${validationKey}-image`}
              />
            )}
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={(theme) => ({
                  margin: theme.spacing(0),
                })}>
                {isMobile && (
                  <CardMedia
                    image={details.image}
                    data-testid={`${validationKey}-image`}
                  />
                )}
                <CardHeader title={details.getName()} />
              </Box>
              <Typography
                variant="subtitle1"
                sx={(theme) => ({
                  marginTop: theme.spacing(1),
                  marginBottom: theme.spacing(2),
                })}>
                {details.getDescription()}
              </Typography>
              {fieldType.toLowerCase().includes("slider") ? (
                <FormCurrencySlider
                  name={`units.[0].unitLevelCoverages.${validationKey}`}
                  control={control}
                  rules={rules?.[validationKey]}
                  step={step}
                  disabled={disabled || loading}
                  options={
                    formatCustomizationOptions(
                      options,
                      min,
                      max
                    ) as GenericOptionType<number>[]
                  }
                />
              ) : (
                <FormSelectField
                  id={`${validationKey}--select`}
                  name={`units.[0].unitLevelCoverages.${validationKey}`}
                  control={control}
                  rules={rules?.[validationKey]}
                  labelHeight={InputLabelHeight.SMALL}
                  tooltip={
                    details.getTooltip({
                      settlementOption: getValues(
                        `units.0.unitLevelCoverages.${validationKey}`
                      ) as SettlementOption,
                    }) || ""
                  }
                  disabled={
                    disabled ||
                    loading ||
                    !options ||
                    options.length < 2 ||
                    (validationKey === "coverageC_settlementOption" &&
                      !covCValue)
                  }
                  label={
                    validationKey.includes("settlementOption")
                      ? "Settlement Type"
                      : ""
                  }
                  options={formatCustomizationOptions(options)}
                  dataTestId={`${validationKey}--select`}
                />
              )}
            </CardContent>
          </Card>
        )
      )}
    </>
  );
}
